import React, { useState } from "react";
import { Button, Card, Col, Input, InputNumber, Modal, Row, Space, Table, Tag, TreeSelect, Typography, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchItemTypes, fetchStockItems } from "../queries/stockQueriesV2";
import { IItemsInStock } from "@drone-parts.garphild.pro/interfaces";
import { BugOutlined, SearchOutlined, SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import RestApiQuery from "../../helpers/rest-api-query";
import { useAtom, useSetAtom } from "jotai";
import { 
  selectedStockItemIdsAtom,
  setSelectedStockItemIdsAtom,
  clearSelectedStockItemsAtom 
} from "../stores/stockStore";

interface ActionDialogState {
  visible: boolean;
  itemId: string;
  action: 'broken' | 'sent' | null;
  quantity: number;
  maxQuantity: number;
  loading: boolean;
}

const { Title } = Typography;

function StockPageV2() {
  // State for filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [actionDialog, setActionDialog] = useState<ActionDialogState>({
    visible: false,
    itemId: '',
    action: null,
    quantity: 1,
    maxQuantity: 1,
    loading: false
  });
  
  // Jotai state for selected item IDs
  const [selectedItemIds] = useAtom(selectedStockItemIdsAtom);
  const setSelectedIds = useSetAtom(setSelectedStockItemIdsAtom);
  const clearSelectedItems = useSetAtom(clearSelectedStockItemsAtom);
  
  const queryClient = useQueryClient();
  
  // Handle row selection
  const rowSelection = {
    selectedRowKeys: selectedItemIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedIds(selectedRowKeys as string[]);
    },
    getCheckboxProps: (record: IItemsInStock) => ({
      disabled: false, // Column configuration not to be checked
      name: record._id,
    } as const),
  };
  
  const { mutate: updateStock } = useMutation(
    async ({ itemId, action, quantity }: { itemId: string; action: 'broken' | 'sent'; quantity: number }) => {
      await RestApiQuery.patch(`/api/stock/${itemId}`, { action, quantity });
    },
    {
      onSuccess: () => {
        message.success(`Item marked as ${actionDialog.action} successfully`);
        queryClient.invalidateQueries(['stockItems']);
        setActionDialog(prev => ({ ...prev, visible: false, loading: false }));
      },
      onError: () => {
        message.error(`Failed to update item status`);
        setActionDialog(prev => ({ ...prev, loading: false }));
      }
    }
  );
  
  const handleOpenActionDialog = (item: IItemsInStock, action: 'broken' | 'sent') => {
    setActionDialog({
      visible: true,
      itemId: item._id,
      action,
      quantity: 1,
      maxQuantity: item.realInStock,
      loading: false
    });
  };
  
  const handleActionConfirm = () => {
    const { itemId, action, quantity } = actionDialog;
    if (!action || !itemId || !quantity) return;
    
    setActionDialog(prev => ({ ...prev, loading: true }));
    updateStock({ itemId, action, quantity });
  };
  
  // Fetch item types for the filter
  const { data: itemTypes = [], isLoading: isTypesLoading } = useQuery(
    ["itemTypes"],
    fetchItemTypes,
    { staleTime: 5 * 60 * 1000 } // 5 minutes
  );
  
  // Fetch stock items based on filters
  const { data: stockItems = [], isLoading: isItemsLoading } = useQuery(
    ["stockItems", { search: searchTerm, types: selectedTypes }],
    fetchStockItems,
    { staleTime: 30 * 1000 } // 30 seconds
  );
  
  const columns = [
    {
      title: "Image",
      dataIndex: "itemUrl",
      key: "image",
      render: (text: string) => (
        <div style={{ width: 60, height: 60, overflow: "hidden" }}>
          {text && <img 
            src={text.includes("http") ? text : `https://${text}`} 
            alt="Item" 
            style={{ width: "100%", height: "auto", objectFit: "cover" }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />}
        </div>
      ),
      width: 80,
    },
    {
      title: "Name",
      dataIndex: "title",
      key: "title",
      render: (text: string, record: IItemsInStock) => (
        <div>
          <div>{record.shortName || text}</div>
          {record.shortName && <div style={{ fontSize: 12, color: "#888" }}>{text}</div>}
          {record.lastInventoryDate && <div style={{ fontSize: 12, color: "#888" }}>{dayjs(record.lastInventoryDate).format("YYYY-MM-DD")}</div>}
          {record.itemVariant && <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>{record.itemVariant}</div>}
        </div>
      ),
      sorter: (a: IItemsInStock, b: IItemsInStock) => a.title.localeCompare(b.title),
    },
    {
      title: "Part Types",
      dataIndex: "partTypes",
      key: "partTypes",
      render: (text: string, record: IItemsInStock) => (
        <div>
          {record.partTypes?.map((partType, index) => (
            <Tag key={index} color="blue">{partType}</Tag>
          ))}
        </div>
      ),
      sorter: (a: IItemsInStock, b: IItemsInStock) => a.itemCategory.localeCompare(b.itemCategory),
      width: 220,
    },
    {
      title: "Price",
      dataIndex: "itemRealPrice",
      key: "itemRealPrice",
      render: (text: number, record: IItemsInStock) => (
        <div>
          {(text || record.itemPrice).toFixed(2)} {record.itemCurrency}
        </div>
      ),
      sorter: (a: IItemsInStock, b: IItemsInStock) => a.itemRealPrice - b.itemRealPrice,
      width: 120,
    },
    {
      title: "In Stock",
      dataIndex: "realInStock",
      key: "realInStock",
      render: (text: number) => <Tag color={text > 5 ? "green" : "orange"}>{text.toFixed(2)}</Tag>,
      sorter: (a: IItemsInStock, b: IItemsInStock) => a.realInStock - b.realInStock,
      width: 100,
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (text: string) => dayjs(text).format("YYYY-MM-DD"),
      sorter: (a: IItemsInStock, b: IItemsInStock) => {
        return dayjs(a.orderDate).unix() - dayjs(b.orderDate).unix();
      },
      width: 120,
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (text: string) => {
        const color = 
          text === "Delivered" ? "green" : 
          text === "Shipped" ? "blue" :
          text === "Processing" ? "orange" : "gray";
        return <Tag color={color}>{text}</Tag>;
      },
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: IItemsInStock) => (
        <Space size="middle">
          <Button 
            icon={<BugOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleOpenActionDialog(record, 'broken');
            }}
            title="Mark as broken"
            danger
          />
          <Button 
            icon={<SendOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleOpenActionDialog(record, 'sent');
            }}
            title="Mark as sent"
            type="primary"
          />
        </Space>
      ),
      fixed: 'right' as const,
    },
  ];
  
  return (
    <div style={{ padding: 24 }}>
      <Modal
        title={`Mark as ${actionDialog.action}`}
        open={actionDialog.visible}
        onOk={handleActionConfirm}
        onCancel={() => setActionDialog(prev => ({ ...prev, visible: false }))}
        confirmLoading={actionDialog.loading}
        okText="Confirm"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Quantity (max: {actionDialog.maxQuantity}):</div>
          <InputNumber
            min={1}
            max={actionDialog.maxQuantity}
            value={actionDialog.quantity}
            onChange={(value) => setActionDialog(prev => ({ ...prev, quantity: value || 1 }))}
            style={{ width: '100%' }}
          />
        </div>
      </Modal>
      <Title level={2}>Stock Management</Title>
      
      <Row gutter={[24, 24]}>
        {/* Filters Section */}
        <Col xs={24} lg={6}>
          <Card title="Filters" loading={isTypesLoading}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <div style={{ marginBottom: 8 }}>Search</div>
                <Input 
                  placeholder="Search items..." 
                  suffix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </div>
              
              <div>
                <div style={{ marginBottom: 8 }}>Item Types</div>
                <TreeSelect
                  treeData={itemTypes.map(type => ({
                    ...type,
                    title: type.title,
                    value: type.title,
                  }))}
                  placeholder="Filter by types"
                  style={{ width: "100%" }}
                  treeCheckable
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  value={selectedTypes}
                  onChange={(value, ...rest) => {
                    console.log("Selected types:", value,)
                    setSelectedTypes(value)
                  }}
                  treeNodeFilterProp="title"
                  allowClear
                />
              </div>
            </Space>
          </Card>
        </Col>
        
        {/* Items List Section */}
        <Col xs={24} lg={18}>
          <Card>
            {selectedItemIds.length > 0 && (
              <div style={{ marginBottom: 16, padding: '8px 16px', background: '#f0f7ff', borderRadius: 4, border: '1px solid #d0e3ff' }}>
                <Space>
                  <span><strong>{selectedItemIds.length}</strong> item{selectedItemIds.length > 1 ? 's' : ''} selected</span>
                  <Button 
                    type="link" 
                    onClick={() => clearSelectedItems()}
                    size="small"
                  >
                    Clear selection
                  </Button>
                  <Button 
                    type="link" 
                    size="small"
                  >
                    Create new product
                  </Button>
                </Space>
              </div>
            )}
            <Table 
              rowSelection={{
                type: 'checkbox',
                ...rowSelection,
              }}
              columns={columns} 
              dataSource={stockItems?.map(item => ({ ...item, key: item._id })) || []} 
              loading={isItemsLoading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
              rowKey="_id"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default StockPageV2;
